import { useCallback, useEffect, useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useAtomValue, useSetAtom } from 'jotai';

import type { ImageAnalyses, TImageType } from '@/@types/latestImagesV2';
import { binFilterAtom } from '@/modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import {
  combineTreatmentsWithImage,
  getImageTeethIdsForSelectedTreatments,
} from '@/modules/claims/helpers';
import { getFindings } from '@/modules/claims/images/helpers';
import {
  claimAttachmentToothLevelImagesAtom,
  selectedTreatmentsAtom,
} from '@/modules/claims/summary/state';
import type { GetClaimSummaryResponse } from '@/modules/insuranceExport/types';
import { TOOTH_LEVEL_FINDING_TYPES } from '@/modules/insuranceExport/types';
import { capitalize } from '@/utils/string';

import { ImageThumbnailPairItem } from './ImageThumbnailPairItem';

type ToothLevelImagesGroupProps = {
  claimSummary?: GetClaimSummaryResponse;
  filteredRadiographs: ImageAnalyses[];
  filteredColorImages: ImageAnalyses[];
};

export const ToothLevelImagesGroup = ({
  claimSummary,
  filteredRadiographs,
  filteredColorImages,
}: ToothLevelImagesGroupProps) => {
  // atoms
  const setClaimAttachmentPdfImages = useSetAtom(
    claimAttachmentToothLevelImagesAtom,
  );

  const selectedTreatments = useAtomValue(selectedTreatmentsAtom);
  const binFilterValue = useAtomValue(binFilterAtom);

  // hooks
  const { t } = useLingui();

  // variables
  const { imagesDisplay: claimSummaryImagesDisplay, treatments } =
    claimSummary ?? {};

  // Image type mapping with translations
  const imageTypeMap = useMemo(
    () => ({
      COLOR_IMAGE: t`Intraoral Photo`,
      BITEWING: t`Bitewing`,
      PERIAPICAL: t`Periapical`,
      PANORAMIC: t`Panoramic`,
    }),
    [t],
  );

  // functions
  const getDmfPercentage = (dmf: number | null) =>
    !dmf ? '--' : `${(dmf * 100).toFixed(0)}%`;

  const getImageTitle = useCallback(
    (toothIds: string[], imageType: TImageType | null) => {
      const toothNumbers =
        toothIds.length > 0 ? `${t`Tooth`} # ${toothIds.join(', ')}` : '';
      const imageTypeTitle = imageType
        ? (imageTypeMap[imageType] ?? capitalize(imageType.toLowerCase()))
        : '';

      if (toothNumbers && imageTypeTitle) {
        return `${toothNumbers} - ${imageTypeTitle}`;
      } else if (toothNumbers) {
        return toothNumbers;
      } else if (imageTypeTitle) {
        return imageTypeTitle;
      } else {
        return '';
      }
    },
    [t, imageTypeMap],
  );

  // Store data needed for PDF generation in atom
  useEffect(() => {
    const allImages = [
      ...(filteredRadiographs ?? []),
      ...(filteredColorImages ?? []),
    ];
    const imagesData = allImages.map((image) => {
      const matchingDisplayItem = claimSummaryImagesDisplay?.find(
        (item) => item.id === image.imageId,
      );
      const imageTeethIds = getImageTeethIdsForSelectedTreatments(
        matchingDisplayItem,
        selectedTreatments,
      );

      // Generate tableData and filteredImageFindings for the PDF image
      const { tableData, filteredImageFindings } = combineTreatmentsWithImage({
        image,
        claimSummaryImagesDisplay: claimSummaryImagesDisplay ?? [],
        selectedTreatmentIds: selectedTreatments ?? [],
        treatments: treatments ?? [],
      });

      // Execute the annotatorFindings logic for each image using filteredImageFindings
      const options = {
        cariesBin: binFilterValue,
        enabledFindings: TOOTH_LEVEL_FINDING_TYPES,
        includedTeethIds: imageTeethIds,
        showIncipientCariesRed: true,
      };
      const annotations = getFindings(filteredImageFindings, options);

      // Check if treatments table should be shown (same logic as ImageThumbnailPairItem)
      const shouldShowTreatmentsTable = tableData.some(
        (item) =>
          (item.caries && item.caries.length > 0) ||
          (item.dmf && item.dmf > 0) ||
          (item.restorativeAdjacentLucency &&
            item.restorativeAdjacentLucency.length > 0),
      );

      // Transform tableData to findings format only if table should be shown
      const findings = shouldShowTreatmentsTable
        ? tableData.map((tooth) => ({
            tooth: tooth.toothId,
            txCodes: Array.from(tooth.txCodes),
            dmf: getDmfPercentage(tooth.dmf),
            caries: (tooth.caries && tooth.caries.length > 0) || false,
            resto:
              (tooth.restorativeAdjacentLucency &&
                tooth.restorativeAdjacentLucency.length > 0) ||
              false,
          }))
        : [];

      return {
        title: getImageTitle(imageTeethIds, image.imageType),
        imageId: image.imageId,
        annotations,
        tableData: findings,
      };
    });
    setClaimAttachmentPdfImages(imagesData);
  }, [
    filteredRadiographs,
    filteredColorImages,
    selectedTreatments,
    claimSummaryImagesDisplay,
    treatments,
    binFilterValue,
    getImageTitle,
    setClaimAttachmentPdfImages,
  ]);

  const displayImages = (images: ImageAnalyses[]) => {
    return images?.map((image) => {
      // Find matching item in claimSummaryImagesDisplay by imageId
      const matchingDisplayItem = claimSummaryImagesDisplay?.find(
        (item) => item.id === image.imageId,
      );
      const imageTeethIds = getImageTeethIdsForSelectedTreatments(
        matchingDisplayItem,
        selectedTreatments,
      );

      // Generate tableData for the image
      const { tableData, filteredImageFindings } = combineTreatmentsWithImage({
        image,
        claimSummaryImagesDisplay: claimSummaryImagesDisplay ?? [],
        selectedTreatmentIds: selectedTreatments ?? [],
        treatments: treatments ?? [],
      });

      return (
        <div
          key={`image-${image.imageId}`}
          className="vai-w-full vai-rounded-lg vai-border vai-border-slate-200 vai-overflow-hidden"
        >
          <h3 className="vai-text-left vai-w-full vai-border-b vai-border-slate-200 vai-bg-slate-100 vai-p-2 vai-text-sm vai-font-semibold">
            {getImageTitle(imageTeethIds, image.imageType)}
          </h3>
          <ImageThumbnailPairItem
            image={image}
            findingTypes={TOOTH_LEVEL_FINDING_TYPES}
            includedTeethIds={imageTeethIds}
            tableData={tableData}
            filteredImageFindings={filteredImageFindings}
          />
        </div>
      );
    });
  };

  return (
    <div
      
      className="vai-flex vai-flex-col vai-items-center vai-w-full vai-gap-8"
    >
      {displayImages(filteredRadiographs)}
      {displayImages(filteredColorImages)}
    </div>
  );
};
